export interface FocalPoint {
  x: number
  y: number
}

export interface SourceRect {
  x: number
  y: number
  width: number
  height: number
}

export interface RenderedImageBounds {
  left: number
  top: number
  width: number
  height: number
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

export function clampFocalPoint(focal: FocalPoint): FocalPoint {
  return {
    x: clamp(focal.x, 0, 1),
    y: clamp(focal.y, 0, 1),
  }
}

export function coverSourceRect(
  sourceWidth: number,
  sourceHeight: number,
  destinationWidth: number,
  destinationHeight: number,
  focal: FocalPoint,
): SourceRect {
  if (
    !Number.isFinite(sourceWidth) ||
    !Number.isFinite(sourceHeight) ||
    !Number.isFinite(destinationWidth) ||
    !Number.isFinite(destinationHeight) ||
    sourceWidth <= 0 ||
    sourceHeight <= 0 ||
    destinationWidth <= 0 ||
    destinationHeight <= 0
  ) {
    throw new Error('image_dimensions')
  }

  const sourceRatio = sourceWidth / sourceHeight
  const destinationRatio = destinationWidth / destinationHeight
  let width = sourceWidth
  let height = sourceHeight

  if (sourceRatio > destinationRatio) {
    width = sourceHeight * destinationRatio
  } else {
    height = sourceWidth / destinationRatio
  }

  const bounded = clampFocalPoint(focal)
  const centerX = bounded.x * sourceWidth
  const centerY = bounded.y * sourceHeight
  return {
    x: clamp(centerX - width / 2, 0, sourceWidth - width),
    y: clamp(centerY - height / 2, 0, sourceHeight - height),
    width,
    height,
  }
}

export function containedImageBounds(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
): RenderedImageBounds {
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    imageWidth <= 0 ||
    imageHeight <= 0
  ) {
    throw new Error('image_dimensions')
  }
  const scale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight)
  const width = imageWidth * scale
  const height = imageHeight * scale
  return {
    left: (containerWidth - width) / 2,
    top: (containerHeight - height) / 2,
    width,
    height,
  }
}

export function pointFromRenderedImage(
  clientX: number,
  clientY: number,
  containerLeft: number,
  containerTop: number,
  bounds: RenderedImageBounds,
): FocalPoint {
  return clampFocalPoint({
    x: (clientX - containerLeft - bounds.left) / bounds.width,
    y: (clientY - containerTop - bounds.top) / bounds.height,
  })
}

export function coverPreviewStyle(
  sourceWidth: number,
  sourceHeight: number,
  destinationWidth: number,
  destinationHeight: number,
  focal: FocalPoint,
): { widthPercent: number; heightPercent: number; leftPercent: number; topPercent: number } {
  const source = coverSourceRect(
    sourceWidth,
    sourceHeight,
    destinationWidth,
    destinationHeight,
    focal,
  )
  const leftPercent = (-source.x / source.width) * 100
  const topPercent = (-source.y / source.height) * 100

  return {
    widthPercent: (sourceWidth / source.width) * 100,
    heightPercent: (sourceHeight / source.height) * 100,
    leftPercent: Object.is(leftPercent, -0) ? 0 : leftPercent,
    topPercent: Object.is(topPercent, -0) ? 0 : topPercent,
  }
}
